<template>
  <div class="commission-orders-page">
    <search-commission-orders @submit="submitSearch" />

    <div class="simiot-table-operation-buttons">
      <a-space>
        <a-button
          v-if="isShowMenu('order_summary/commission_orders/export')"
          type="primary"
          @click="exportData"
        >
          导出
        </a-button>
      </a-space>
    </div>

    <div class="commission-orders-table-wrap">
      <a-table
        size="middle"
        :scroll="{ x: 1480 }"
        :columns="columns"
        :data-source="data"
        :loading="loading"
        :pagination="false"
        :expanded-row-keys="expandedRowKeys"
        :expand-row-by-click="false"
        :expand-icon="expandIcon"
        row-key="id"
        @expand="onExpand"
      >
        <template slot="expandedRowRender" slot-scope="record">
          <div v-if="record.detailLoading" class="commission-order-detail commission-order-detail--loading">
            <a-spin size="small" />
            <span class="commission-order-detail__loading-text">加载详情中…</span>
          </div>
          <a-table
            v-else-if="record.detail && detailTableData(record.detail).length"
            size="middle"
            :pagination="false"
            :columns="detailTableColumns(record.detail)"
            :data-source="detailTableData(record.detail)"
            row-key="__rowKey"
          />
          <div v-else-if="record.detail" class="commission-order-detail commission-order-detail--hint">
            暂无详情数据
          </div>
          <div v-else class="commission-order-detail commission-order-detail--hint">
            展开后自动加载详情
          </div>
        </template>
      </a-table>
    </div>

    <pagination
      v-show="pagination.total_count > 0"
      :total-count="pagination.total_count"
      :page.sync="query.page"
      :per-page.sync="query.per_page"
      @change="fetchData"
    />

    <!-- 采购项目详情模态框：只给“项目编号(item_no)”提供可点击入口 -->
    <show-agent-purchase-item
      v-if="isShowPurchaseItemModal"
      :visible.sync="isShowPurchaseItemModal"
      :id="showingPurchaseItemId"
    />
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'
import SearchCommissionOrders from '@/views/order_summary/SearchCommissionOrders'
import Pagination from '@/components/Pagination'
import {
  findOrderSummaryCommissionOrders,
  getOrderSummaryCommissionOrderDetail,
  exportOrderSummaryCommissionOrders
} from '@/api/order_summary'
import { exportExcel } from '@/api/excel'
import { formatCurrency } from '@/utils/filter'

// 与后端 GET .../commission_orders/:id/detail 返回的 attributes 键一致
// 文案与顺序仅前端维护
const COMMISSION_ORDER_DETAIL_FIELDS = {
  agent_purchase_item: [
    { key: 'item_no', label: '项目编号' },
    { key: 'carrier_type', label: '运营商类型' },
    { key: 'user_business_type', label: '业务模式' },
    { key: 'agents_product_name', label: '客户套餐' },
    { key: 'service_period', label: '服务周期(月)' },
    { key: 'card_count', label: '开卡数量' },
    { key: 'total_fee', label: '结算金额(元)', format: 'currency' },
    { key: 'commission_fee', label: '佣金金额(元)', format: 'currency' }
  ],
  agent_renew_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'cards_count', label: '卡号数量' },
    { key: 'cycles', label: '续期周期' },
    { key: 'batch_operation_no', label: '批量操作批次' },
    { key: 'total_fee', label: '结算金额(元)', format: 'currency' },
    { key: 'commission_fee', label: '佣金金额(元)', format: 'currency' }
  ],
  agent_active_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'cards_count', label: '卡号数量' },
    { key: 'batch_operation_no', label: '批量操作批次' },
    { key: 'total_fee', label: '结算金额(元)', format: 'currency' },
    { key: 'commission_fee', label: '佣金金额(元)', format: 'currency' }
  ],
  agent_subscribe_card_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'cards_count', label: '卡号数量' },
    { key: 'cycles', label: '订购周期' },
    { key: 'batch_operation_no', label: '批量操作批次' },
    { key: 'total_fee', label: '结算金额(元)', format: 'currency' },
    { key: 'commission_fee', label: '佣金金额(元)', format: 'currency' }
  ],
  agent_product_usage_order: [
    { key: 'agents_product_name', label: '客户套餐' },
    { key: 'bill_month', label: '账单月份' },
    { key: 'usage_order_status', label: '账单状态' },
    { key: 'total_fee', label: '结算金额(元)', format: 'currency' },
    { key: 'commission_fee', label: '佣金金额(元)', format: 'currency' }
  ],
  agent_allocation_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'cards_count', label: '卡号数量' },
    { key: 'total_fee', label: '结算金额(元)', format: 'currency' },
    { key: 'commission_fee', label: '佣金金额(元)', format: 'currency' }
  ],
  agent_product_stop_order: [
    { key: 'agents_product_name', label: '客户套餐' },
    { key: 'bill_month', label: '账单月份' },
    { key: 'stop_order_status', label: '账单状态' },
    { key: 'cards_count', label: '停机保号卡量' },
    { key: 'total_fee', label: '结算金额(元)', format: 'currency' },
    { key: 'commission_fee', label: '佣金金额(元)', format: 'currency' }
  ],
  agent_subscribe_traffic_pool_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'traffic_pool_product_name', label: '流量池套餐' },
    { key: 'cycles', label: '订购周期' },
    { key: 'operator_name', label: '操作人' },
    { key: 'total_fee', label: '结算金额(元)', format: 'currency' },
    { key: 'commission_fee', label: '佣金金额(元)', format: 'currency' }
  ]
}

export default {
  name: 'OrderSummaryCommissionOrders',
  components: {
    SearchCommissionOrders,
    Pagination,
    ShowAgentPurchaseItem: () => import('@/views/agent_monthly_bills/purchase/Show')
  },
  data() {
    return {
      data: [],
      loading: true,
      pagination: {
        total_count: 0
      },
      query: {},
      expandedRowKeys: [],
      isShowPurchaseItemModal: false,
      showingPurchaseItemId: 0
    }
  },
  computed: {
    columns() {
      return [
        { title: '客户名称', dataIndex: 'agent_name', width: 160 },
        { title: '订单类型', dataIndex: 'order_type_display', width: 150 },
        { title: '订单月份', dataIndex: 'order_month', width: 88 },
        { title: '批次', dataIndex: 'batch_no', width: 240 },
        {
          title: '结算金额(元)',
          dataIndex: 'total_fee',
          width: 130,
          align: 'right',
          customRender: (text) => (text != null && text !== '' ? formatCurrency(text) : '')
        },
        {
          title: '佣金(元)',
          dataIndex: 'commission_fee',
          width: 120,
          align: 'right',
          customRender: (text) => (text != null && text !== '' ? formatCurrency(text) : '')
        },
        { title: '责任销售', dataIndex: 'sale_user_name', width: 120 },
        { title: '创建时间', dataIndex: 'created_at', width: 170 }
      ]
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    isShowMenu(menu) {
      return hasPermission(menu)
    },

    visibleDetailDefs(detail) {
      if (!detail || !detail.attributes) {
        return []
      }
      const defs = COMMISSION_ORDER_DETAIL_FIELDS[detail.order_type]
      if (!defs || !defs.length) {
        return []
      }
      const attrs = detail.attributes
      return defs.filter((def) => {
        const raw = attrs[def.key]
        if (raw == null || raw === '') {
          return false
        }
        if (typeof raw === 'string' && raw.trim() === '') {
          return false
        }
        const value = this.formatCommissionDetailValue(raw, def.format)
        return value !== '' && value != null
      })
    },

    detailTableColumns(detail) {
      return this.visibleDetailDefs(detail).map((def) => ({
        title: def.label,
        dataIndex: def.key,
        key: def.key,
        customRender: (text) => this.renderDetailCell(detail, def.key, text)
      }))
    },

    detailTableData(detail) {
      const defs = this.visibleDetailDefs(detail)
      if (!defs.length) {
        return []
      }
      const attrs = detail.attributes || {}
      const row = { __rowKey: String(detail.order_type || 'detail') }
      for (const def of defs) {
        row[def.key] = this.formatCommissionDetailValue(attrs[def.key], def.format)
      }
      return [row]
    },

    renderDetailCell(detail, fieldKey, text) {
      const purchaseItemId = this.commissionDetailPurchaseItemId(detail, fieldKey)
      if (purchaseItemId) {
        return (
          <a class='commission-order-detail__link' onClick={() => this.openPurchaseItemDetail(purchaseItemId)}>
            {text}
          </a>
        )
      }
      const link = this.commissionDetailFieldLink(detail, fieldKey)
      if (link) {
        const resolved = this.$router.resolve(link)
        return (
          <a
            class='commission-order-detail__link'
            href={resolved.href}
            target='_blank'
            rel='noopener noreferrer'
          >
            {text}
          </a>
        )
      }
      return text
    },

    commissionDetailBillQuery(detail) {
      const bid = detail.agent_monthly_bill_id
      if (bid == null || bid === '' || Number(bid) === 0) {
        return {}
      }
      return { bill_id: bid }
    },

    commissionDetailFieldLink(detail, fieldKey) {
      if (!detail) return null
      const agentId = detail.agent_id
      if (!agentId) return null

      const q = this.commissionDetailBillQuery(detail)

      const slug = detail.order_type
      const agentParams = { id: agentId }

      switch (slug) {
        case 'agent_renew_order':
          if (fieldKey === 'bill_no' && detail.renew_order_id) {
            return {
              name: 'agent_monthly_bill_renew_order_info_commission',
              params: { ...agentParams, renew_order_id: detail.renew_order_id },
              query: q
            }
          }
          if (fieldKey === 'batch_operation_no' && detail.batch_operation_record_id) {
            return {
              name: 'batch_operation_record_info',
              params: { id: detail.batch_operation_record_id }
            }
          }
          break
        case 'agent_active_order':
          if (fieldKey === 'bill_no' && detail.active_order_id) {
            return {
              name: 'agent_monthly_bill_active_order_info_commission',
              params: { ...agentParams, active_order_id: detail.active_order_id },
              query: q
            }
          }
          if (fieldKey === 'batch_operation_no' && detail.batch_operation_record_id) {
            return {
              name: 'batch_operation_record_info',
              params: { id: detail.batch_operation_record_id }
            }
          }
          break
        case 'agent_subscribe_card_order':
          if (fieldKey === 'bill_no' && detail.subscribe_order_id) {
            return {
              name: 'agent_monthly_bill_subscribe_order_info_commission',
              params: { ...agentParams, subscribe_order_id: detail.subscribe_order_id },
              query: q
            }
          }
          if (fieldKey === 'batch_operation_no' && detail.batch_operation_record_id) {
            return {
              name: 'batch_operation_record_info',
              params: { id: detail.batch_operation_record_id }
            }
          }
          break
        case 'agent_product_usage_order':
          if (fieldKey === 'agents_product_name' && detail.product_usage_order_id) {
            return {
              name: 'agent_monthly_bill_card_use_info_commission',
              params: { ...agentParams, card_use_id: detail.product_usage_order_id },
              query: q
            }
          }
          break
        case 'agent_allocation_order':
          if (fieldKey === 'bill_no' && detail.allocation_batch_operation_id) {
            return {
              name: 'allocation_record_info',
              params: { id: detail.allocation_batch_operation_id }
            }
          }
          break
        case 'agent_product_stop_order':
          if (fieldKey === 'agents_product_name' && detail.product_stop_order_id) {
            return {
              name: 'agent_monthly_bill_stop_info',
              params: { ...agentParams, stop_id: detail.product_stop_order_id },
              query: q
            }
          }
          break
        case 'agent_subscribe_traffic_pool_order':
          if (fieldKey === 'bill_no' && detail.subscribe_traffic_pool_order_id) {
            return {
              name: 'agent_monthly_bill_subscribe_traffic_pool_order_info',
              params: { ...agentParams, subscribe_traffic_pool_order_id: detail.subscribe_traffic_pool_order_id },
              query: q
            }
          }
          break
        default:
          break
      }
      return null
    },

    /**
     * 采购项目详情：仅“项目编号(item_no)”可点击（打开采购项目详情模态框）
     */
    commissionDetailPurchaseItemId(detail, fieldKey) {
      if (!detail || detail.order_type !== 'agent_purchase_item') return 0
      if (fieldKey !== 'item_no') return 0
      if (!this.isShowMenu('agent_purchase_items/show')) return 0
      return detail.purchase_item_id || 0
    },

    openPurchaseItemDetail(purchaseItemId) {
      if (!purchaseItemId) return
      this.showingPurchaseItemId = purchaseItemId
      this.isShowPurchaseItemModal = true
    },

    formatCommissionDetailValue(raw, format) {
      if (format === 'currency') {
        return formatCurrency(Number(raw))
      }
      if (typeof raw === 'number') {
        return String(raw)
      }
      return String(raw)
    },

    expandIcon(props) {
      const { expanded, onExpand, record } = props
      return (
        <a onClick={e => onExpand(record, e)} class='commission-orders-expand-icon'>
          {expanded ? <a-icon type='minus-square' /> : <a-icon type='plus-square' />}
        </a>
      )
    },

    onExpand(expanded, record) {
      if (expanded) {
        if (!this.expandedRowKeys.includes(record.id)) {
          this.expandedRowKeys = [...this.expandedRowKeys, record.id]
        }
        if (record.detail == null && !record.detailLoading) {
          this.$set(record, 'detailLoading', true)
          getOrderSummaryCommissionOrderDetail(record.id).then((res) => {
            if (res.code === 0) {
              this.$set(record, 'detail', res.data)
            } else {
              this.$message.error(res.msg || '加载详情失败')
            }
            this.$set(record, 'detailLoading', false)
          }).catch(() => {
            this.$set(record, 'detailLoading', false)
            this.$message.error('加载详情失败')
          })
        }
      } else {
        this.expandedRowKeys = this.expandedRowKeys.filter((id) => id !== record.id)
      }
    },

    submitSearch(search) {
      this.query = Object.assign(
        {},
        this.$options.data().query,
        { per_page: this.query.per_page || 20 },
        search
      )
      this.fetchData()
    },

    fetchData() {
      this.loading = true
      this.expandedRowKeys = []
      findOrderSummaryCommissionOrders(this.query).then((res) => {
        if (res.code === 0) {
          this.data = res.data
          this.pagination = res.pagination
        }
        this.loading = false
      })
    },

    exportPayload() {
      const { ...rest } = this.query
      return rest
    },

    exportData() {
      exportOrderSummaryCommissionOrders(this.exportPayload()).then((res) => {
        if (res.code === 0 && res.data && res.data.is_direct_download === true) {
          exportExcel(res.data.id, res.data.file_name)
        } else if (res && res.msg) {
          this.$message.warning(res.msg)
        }
      })
    }
  }
}
</script>

<style scoped>
.commission-orders-table-wrap {
  overflow-x: auto;
}

.commission-orders-expand-icon {
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
  color: #1890ff;
}

.commission-order-detail--loading,
.commission-order-detail--hint {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 0;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

.commission-order-detail__loading-text {
  margin-left: 10px;
}

.commission-order-detail__link {
  color: #1890ff;
}
</style>
